import { EntityStatusChip } from '../../../components/entity-detail/EntityStatusChip.js';
import { TABLE_SORTER_TOOLTIP } from '../../../shared/ui/list-styles.js';
import { useI18nSubscription } from '../../i18n/useI18nSubscription.js';
import { i18n } from '../../../i18n/index.js';
// Активные сессии пользователя: где он вошёл и чем закрыть чужой вход.
//
// Раньше выход завершал только текущую сессию: забытая на чужом компьютере или
// украденная жила до истечения срока, и увидеть её было негде.
import { useState, type ReactElement } from 'react';
import { Alert, Button, Popconfirm, Spin, Table, Typography } from 'antd';
import { EmptyState } from '../../../components/entity-detail/EmptyState.js';
import type { ColumnsType } from 'antd/es/table/interface';

import { formatDateTime } from '../../../shared/format/index.js';
import { ErrorState } from '../../../shared/ui/ErrorState.js';
import type { SessionItem } from '../api/sessions.client.js';
import {
  useActiveSessions,
  useRevokeOtherSessionsMutation,
  useRevokeSessionMutation,
} from '../hooks/useSessions.js';

const { Text } = Typography;

/**
 * Строка user-agent целиком нечитаема, а разбирать её по-настоящему — отдельная
 * библиотека ради подписи в таблице. Достаём то, что различает устройства.
 */
function describeDevice(userAgent: string | null): string {
  if (!userAgent)
    return i18n.t(
      'crm.features.features.settings.components.activesessionspanel.неизвестное_устройство',
    );

  const os = /Windows/i.test(userAgent)
    ? 'Windows'
    : /Android/i.test(userAgent)
      ? 'Android'
      : /iPhone|iPad|iOS/i.test(userAgent)
        ? 'iOS'
        : /Mac OS X|Macintosh/i.test(userAgent)
          ? 'macOS'
          : /Linux/i.test(userAgent)
            ? 'Linux'
            : null;

  // Порядок важен: Chrome и Edge представляются и как Safari тоже.
  const browser = /Edg\//i.test(userAgent)
    ? 'Edge'
    : /OPR\//i.test(userAgent)
      ? 'Opera'
      : /Chrome\//i.test(userAgent)
        ? 'Chrome'
        : /Firefox\//i.test(userAgent)
          ? 'Firefox'
          : /Safari\//i.test(userAgent)
            ? 'Safari'
            : null;

  return (
    [browser, os].filter(Boolean).join(' · ') ||
    i18n.t('crm.features.features.settings.components.activesessionspanel.неизвестное_устройство_2')
  );
}

export function ActiveSessionsPanel(): ReactElement {
  useI18nSubscription();
  const sessionsQuery = useActiveSessions();
  const revokeMut = useRevokeSessionMutation();
  const revokeOthersMut = useRevokeOtherSessionsMutation();
  const [revokedCount, setRevokedCount] = useState<number | null>(null);

  const sessions = sessionsQuery.data?.sessions ?? [];
  const others = sessions.filter((session) => !session.current);
  const actionError = (revokeMut.error ?? revokeOthersMut.error) as Error | null;

  const columns: ColumnsType<SessionItem> = [
    {
      title: i18n.t('crm.features.features.settings.components.activesessionspanel.устройство'),
      key: 'device',
      render: (_, session) => (
        <div className="flex flex-col">
          <span className="font-medium text-gray-900">
            {describeDevice(session.userAgent)}
            {session.current ? (
              <EntityStatusChip tone="success" className="ml-2">
                {i18n.t('crm.features.features.settings.components.activesessionspanel.текущая')}
              </EntityStatusChip>
            ) : null}
          </span>
          {session.ip ? <span className="text-xs text-gray-500">{session.ip}</span> : null}
        </div>
      ),
    },
    {
      title: i18n.t(
        'crm.features.features.settings.components.activesessionspanel.последняя_активность',
      ),
      key: 'lastUsedAt',
      width: 200,
      render: (_, session) => (
        <span className="text-sm text-gray-600">
          {session.lastUsedAt ? formatDateTime(session.lastUsedAt) : '—'}
        </span>
      ),
    },
    {
      title: i18n.t('crm.features.features.settings.components.activesessionspanel.вход'),
      key: 'createdAt',
      width: 180,
      render: (_, session) => (
        <span className="text-sm text-gray-600">{formatDateTime(session.createdAt)}</span>
      ),
    },
    {
      title: i18n.t('crm.features.features.settings.components.activesessionspanel.действия'),
      key: 'actions',
      width: 120,
      render: (_, session) =>
        session.current ? (
          // Текущую сессию закрывать отсюда нечем: для этого есть «Выйти» в шапке.
          <Text type="secondary">—</Text>
        ) : (
          <Popconfirm
            title={i18n.t(
              'crm.features.features.settings.components.activesessionspanel.закрыть_эту_сессию',
            )}
            description={i18n.t(
              'crm.features.features.settings.components.activesessionspanel.устройство_сразу_попадет_на_экран_входа',
            )}
            okText={i18n.t('crm.features.features.settings.components.activesessionspanel.закрыть')}
            cancelText={i18n.t(
              'crm.features.features.settings.components.activesessionspanel.отмена',
            )}
            okButtonProps={{
              danger: true,
              loading: revokeMut.isPending && revokeMut.variables === session.id,
            }}
            onConfirm={() => revokeMut.mutateAsync(session.id)}
          >
            <Button size="small" danger>
              {i18n.t('crm.features.features.settings.components.activesessionspanel.закрыть_2')}
            </Button>
          </Popconfirm>
        ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-gray-900">
            {i18n.t(
              'crm.features.features.settings.components.activesessionspanel.активные_сессии',
            )}
          </div>
          <Text type="secondary" style={{ fontSize: 13 }}>
            {i18n.t(
              'crm.features.features.settings.components.activesessionspanel.устройства_на_которых_выполнен_вход_закрой',
            )}
          </Text>
        </div>
        <Popconfirm
          title={i18n.t(
            'crm.features.features.settings.components.activesessionspanel.выйти_на_всех_остальных_устройствах',
          )}
          description={i18n.t(
            'crm.features.features.settings.components.activesessionspanel.текущая_сессия_останется_активной',
          )}
          okText={i18n.t(
            'crm.features.features.settings.components.activesessionspanel.выйти_везде',
          )}
          cancelText={i18n.t(
            'crm.features.features.settings.components.activesessionspanel.отмена_2',
          )}
          okButtonProps={{ danger: true, loading: revokeOthersMut.isPending }}
          onConfirm={async () => {
            const result = await revokeOthersMut.mutateAsync();
            setRevokedCount(result.count);
          }}
          disabled={others.length === 0}
        >
          <Button danger disabled={others.length === 0}>
            {i18n.t(
              'crm.features.features.settings.components.activesessionspanel.выйти_на_других_устройствах',
            )}
          </Button>
        </Popconfirm>
      </div>

      {actionError ? (
        <Alert
          type="error"
          showIcon
          closable
          title={i18n.t(
            'crm.features.features.settings.components.activesessionspanel.не_удалось_закрыть_сессию',
          )}
          description={actionError.message}
        />
      ) : null}

      {revokedCount !== null ? (
        <Alert
          type="success"
          showIcon
          closable
          onClose={() => setRevokedCount(null)}
          title={
            revokedCount === 0
              ? i18n.t(
                  'crm.features.features.settings.components.activesessionspanel.других_активных_сессий_не_было',
                )
              : i18n.t('crm.features.dynamic.features.settings.components.activesessionspanel.72', {
                  p0: revokedCount,
                })
          }
        />
      ) : null}

      {sessionsQuery.isError ? (
        <ErrorState
          title={i18n.t(
            'crm.features.features.settings.components.activesessionspanel.не_удалось_загрузить_сессии',
          )}
          error={sessionsQuery.error}
          onRetry={() => void sessionsQuery.refetch()}
        />
      ) : sessionsQuery.isLoading ? (
        <div className="flex justify-center py-6">
          <Spin />
        </div>
      ) : sessions.length === 0 ? (
        <EmptyState
          text={i18n.t(
            'crm.features.features.settings.components.activesessionspanel.активных_сессий_нет',
          )}
        />
      ) : (
        <Table<SessionItem>
          showSorterTooltip={TABLE_SORTER_TOOLTIP}
          size="small"
          rowKey="id"
          dataSource={sessions}
          columns={columns}
          pagination={false}
        />
      )}
    </div>
  );
}
